import {
  CommonDefaults,
  CommonRegistry,
  EquipSlot,
  FacingDirection,
} from '@common/components/components';

export type { FacingDirection };

export interface PixiSpriteData {
  /** Whether the sprite has been initialized in PixiInstanceService */
  initialized: boolean;
  lastTextureId: string;
}

/**
 * Animation playback state component.
 * Selection systems (e.g., WalkingAnimationSystem) set `name`.
 * AnimationSystem advances timing and updates frame index.
 */
export interface AnimationData {
  /** Animation tag name (from Pixelorama) */
  name: string;
  /** 1-based frame index within the animation */
  animationFrame: number;
  /** Accumulated time for frame timing (ms) */
  timer: number;
  /** Whether the animation should loop */
  loop: boolean;
  /** Whether the animation is currently playing */
  isPlaying: boolean;
  /** Current frame duration (ms), recalculated on frame change for random durations */
  currentFrameDurationMs?: number;
}

/**
 * Interpolated transform offset applied during animation playback.
 * Values are computed by AnimationTransformSystem based on frame user_data.
 * Applied by RenderSystem as additive offsets to the base transform.
 */
export interface AnimationTransformOffsetData {
  /** X position offset in pixels */
  x: number;
  /** Y position offset in pixels */
  y: number;
  /** Scale X multiplier (1.0 = no change) */
  scaleX: number;
  /** Scale Y multiplier (1.0 = no change) */
  scaleY: number;
  /** Rotation offset in radians */
  rotation: number;
}

export interface VisualPositionData {
  x: number;
  y: number;
}

export interface NetworkIdentityData {
  serverID: number;
}

/**
 * Camera follow target component.
 * The camera will track the position of the target entity.
 */
export interface CameraFollowData {
  targetEntityId: number;
}

/**
 * Client-only marker for visual representations of equipped items.
 * Tracks which equipment slot this visual represents.
 * Owner is tracked via ChildOf.root.
 */
export interface EquippedVisualData {
  /** The equipment slot this visual represents */
  slot: EquipSlot;
  /** The client entity ID of the equipped item (for detecting swaps) */
  clientItemEntityId: number;
}

export const ClientDefaults = {
  ...CommonDefaults,
  VisualPosition: {
    x: 0,
    y: 0,
  },
  AnimationTransformOffset: {
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
  },
};

export interface ClientRegistry extends CommonRegistry {
  PixiSprite: PixiSpriteData;
  Animation: AnimationData;
  VisualPosition: VisualPositionData;
  NetworkIdentity: NetworkIdentityData;
  CameraFollow: CameraFollowData;
  EquippedVisual: EquippedVisualData;

  AnimationTransformOffset: AnimationTransformOffsetData;

  readonly __defaults: typeof ClientDefaults;
}
